import { format, parseISO, isValid } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Safely format a date value using date-fns with the German locale.
 *
 * Accepts an ISO 8601 string, an epoch-millis number, or a `Date` object so
 * every page/component can route date rendering through this single guard
 * (see the ESLint `no-restricted-imports` rule in package.json that forbids
 * raw `parseISO`/`format` in pages/** and components/**).
 *
 * Returns the placeholder '—' when:
 *   - `value` is missing (undefined, null, empty string), OR
 *   - the parsed value is an invalid Date (e.g. 'not-a-date', `new Date(NaN)`).
 *
 * @param value An ISO 8601 date/datetime string, epoch millis, a `Date`, or a
 *              missing sentinel (undefined/null/'').
 * @param fmt   A date-fns format string (default 'dd.MM.yyyy').
 * @returns     Formatted date string, or '—' for missing/invalid input.
 */
export function formatDateSafe(
  value?: string | number | Date | null,
  fmt = 'dd.MM.yyyy',
): string {
  if (value === undefined || value === null || value === '') return '—';
  const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
  if (!isValid(parsed)) return '—';
  return format(parsed, fmt, { locale: de });
}

/**
 * The parsing half of the same guard (NC-1269).
 *
 * `formatDateSafe` covers rendering, but date *arithmetic* — which month does a
 * run fall into, do two runs overlap — needs a `Date`, and the ESLint
 * `no-restricted-imports` rule deliberately keeps raw `parseISO` out of
 * pages/** and components/**. Without a parsing counterpart here, a caller's
 * only options are to violate that rule or to hand-roll `new Date(str)`, which
 * differs from `parseISO` on date-only input (UTC vs. local midnight) and would
 * put runs in the wrong day near midnight.
 *
 * Returns `null` — never an Invalid Date — for missing or unparseable input, so
 * callers get a value they must handle rather than one that silently poisons
 * every comparison it touches (`NaN` compares false against everything).
 */
export function parseDateSafe(value?: string | number | Date | null): Date | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(parsed) ? parsed : null;
}

/** True when `value` is a date-only ISO string ('2026-09-10'), i.e. no time part. */
export function isDateOnlyIso(value?: string | number | Date | null): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}
